import React from "react";
import { useNavigate } from "react-router-dom";

function ProblemsList({
  problemsWithStats,
  filteredProblems,
  currentProblems,
  currentPage,
  totalPages,
  indexOfFirstProblem,
  indexOfLastProblem,
  ratingRange,
  setRatingRange,
  selectedTags,
  setSelectedTags,
  tagMode,
  setTagMode,
  searchQuery,
  setSearchQuery,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  statusFilter,
  setStatusFilter,
  handleTagClick,
  handlePageChange,
  handleRefreshData,
  toggleDarkMode,
  getRatingColor,
  getDifficultyText,
  isDarkMode,
  availableTags,
  username,
  onUsernameChange,
  userSubmissionsLoading,
  userSubmissionsError,
}) {
  const navigate = useNavigate();

  const openProblemDetails = (problem) => {
    navigate(`/problem/${problem.contestId}/${problem.index}`);
  };

  return (
    <div
      className={`min-h-screen font-['Inter'] transition-all duration-500 ${
        isDarkMode ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100" : "bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50"
      }`}
    >
      {/* Header */}
      <header className={`backdrop-blur-xl border-b transition-all duration-500 ${isDarkMode ? "bg-slate-900/80 border-slate-700/50" : "bg-white/80 border-slate-200/50"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center py-8">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/")}
                className={`p-3 rounded-2xl transition-all duration-300 hover:scale-110 ${
                  isDarkMode
                    ? "bg-gradient-to-br from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 text-slate-300"
                    : "bg-gradient-to-br from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 text-slate-600"
                }`}
                title="Back to Home"
              >
                <i className="fas fa-arrow-left text-xl"></i>
              </button>
              <div className={`p-3 rounded-2xl ${isDarkMode ? "bg-gradient-to-br from-blue-500 to-purple-600" : "bg-gradient-to-br from-blue-600 to-indigo-700"}`}>
                <i className="fas fa-search text-white text-xl"></i>
              </div>
              <div>
                <h1 className={`text-3xl font-bold bg-gradient-to-r ${isDarkMode ? "from-slate-100 to-slate-300" : "from-slate-900 to-slate-700"} bg-clip-text text-transparent`}>
                  Problem Selector
                </h1>
                <p className={`text-sm mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Advanced problem filtering & discovery</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className={`px-4 py-2 rounded-xl text-sm font-medium ${isDarkMode ? "bg-slate-800/50 text-slate-300" : "bg-slate-100/80 text-slate-600"}`}>
                <i className="fas fa-database mr-2"></i>
                {problemsWithStats.length.toLocaleString()} problems
              </div>
              <div className={`px-4 py-2 rounded-xl text-sm font-medium ${isDarkMode ? "bg-slate-800/50 text-slate-300" : "bg-slate-100/80 text-slate-600"}`}>
                <i className="fas fa-user mr-2"></i>
                {username}
              </div>
              <button
                onClick={() => navigate("/stats")}
                className={`px-4 py-2 rounded-xl transition-all duration-300 hover:scale-105 font-medium ${
                  isDarkMode
                    ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                    : "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                }`}
                title="View user statistics"
              >
                <i className="fas fa-chart-bar mr-2"></i>
                Stats
              </button>
              <button
                onClick={toggleDarkMode}
                className={`p-3 rounded-xl transition-all duration-300 hover:scale-105 ${
                  isDarkMode ? "bg-slate-800/50 hover:bg-slate-700/50 text-slate-300" : "bg-slate-100/80 hover:bg-slate-200/80 text-slate-600"
                }`}
                title="Toggle dark mode"
              >
                <i className={`fas ${isDarkMode ? "fa-sun" : "fa-moon"}`}></i>
              </button>
              <button
                onClick={handleRefreshData}
                className={`px-4 py-2 rounded-xl transition-all duration-300 hover:scale-105 font-medium ${
                  isDarkMode
                    ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                    : "bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white"
                }`}
                title="Refresh data from Codeforces"
              >
                <i className="fas fa-sync-alt mr-2"></i>
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Filters Section */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div
          className={`rounded-3xl backdrop-blur-xl overflow-hidden mb-8 transition-all duration-500 ${
            isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/80 border border-slate-200/50 shadow-xl"
          }`}
        >
          {/* Modernized Header */}
          <div
            className={`px-8 py-6 border-b ${
              isDarkMode ? "border-slate-700/50 bg-gradient-to-r from-slate-800/80 to-slate-900/80" : "border-slate-200/50 bg-gradient-to-r from-slate-50/80 to-blue-50/80"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className={`p-4 rounded-2xl ${isDarkMode ? "bg-gradient-to-br from-blue-500/20 to-purple-600/20" : "bg-gradient-to-br from-blue-100 to-purple-100"}`}>
                  <i className={`fas fa-sliders-h text-2xl ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}></i>
                </div>
                <div>
                  <h2 className={`text-3xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Filters & Sorting</h2>
                  <p className={`text-sm mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Customize your problem search experience</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* Active filters count */}
                {(ratingRange.min || ratingRange.max || selectedTags.length > 0 || searchQuery || statusFilter !== "all") && (
                  <div
                    className={`px-4 py-2 rounded-xl font-medium ${
                      isDarkMode ? "bg-blue-500/20 text-blue-300 border border-blue-500/30" : "bg-blue-100 text-blue-700 border border-blue-200"
                    }`}
                  >
                    <i className="fas fa-filter mr-2"></i>
                    {[ratingRange.min || ratingRange.max ? 1 : 0, selectedTags.length, searchQuery ? 1 : 0, statusFilter !== "all" ? 1 : 0].reduce((a, b) => a + b, 0)} active
                  </div>
                )}
                {/* Quick clear all button */}
                <button
                  onClick={() => {
                    setRatingRange({ min: "", max: "" });
                    setSelectedTags([]);
                    setSearchQuery("");
                    setStatusFilter("all");
                  }}
                  className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 hover:scale-105 ${
                    isDarkMode ? "bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30" : "bg-red-100 text-red-700 border border-red-200 hover:bg-red-200"
                  }`}
                  title="Clear all filters"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Filter Content */}
          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
              {/* Search Card */}
              <div
                className={`rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] ${
                  isDarkMode ? "bg-slate-700/30 border border-slate-600/50" : "bg-slate-50/50 border border-slate-200/50"
                }`}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`p-2 rounded-xl ${isDarkMode ? "bg-blue-500/20" : "bg-blue-100"}`}>
                    <i className={`fas fa-search ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}></i>
                  </div>
                  <h3 className={`font-bold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>Search</h3>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Name, index, contest..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 ${
                      isDarkMode ? "bg-slate-800/70 border-slate-600/50 text-slate-100 placeholder-slate-400" : "bg-white border-slate-200 text-slate-900 placeholder-slate-500"
                    }`}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-sm ${
                        isDarkMode ? "text-slate-400 hover:text-slate-200" : "text-slate-400 hover:text-slate-600"
                      }`}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Rating Range Card */}
              <div
                className={`rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] ${
                  isDarkMode ? "bg-slate-700/30 border border-slate-600/50" : "bg-slate-50/50 border border-slate-200/50"
                }`}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`p-2 rounded-xl ${isDarkMode ? "bg-yellow-500/20" : "bg-yellow-100"}`}>
                    <i className="fas fa-star text-yellow-500"></i>
                  </div>
                  <h3 className={`font-bold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>Rating Range</h3>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Min</label>
                      <input
                        type="number"
                        placeholder="800"
                        value={ratingRange.min}
                        onChange={(e) => setRatingRange({ ...ratingRange, min: e.target.value })}
                        className={`w-full px-3 py-2 rounded-lg border transition-all duration-300 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 ${
                          isDarkMode ? "bg-slate-800/70 border-slate-600/50 text-slate-100 placeholder-slate-400" : "bg-white border-slate-200 text-slate-900 placeholder-slate-500"
                        }`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Max</label>
                      <input
                        type="number"
                        placeholder="3500"
                        value={ratingRange.max}
                        onChange={(e) => setRatingRange({ ...ratingRange, max: e.target.value })}
                        className={`w-full px-3 py-2 rounded-lg border transition-all duration-300 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 ${
                          isDarkMode ? "bg-slate-800/70 border-slate-600/50 text-slate-100 placeholder-slate-400" : "bg-white border-slate-200 text-slate-900 placeholder-slate-500"
                        }`}
                      />
                    </div>
                  </div>
                  {(ratingRange.min || ratingRange.max) && (
                    <div
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        isDarkMode ? "bg-blue-500/10 border border-blue-500/20" : "bg-blue-50 border border-blue-200"
                      }`}
                    >
                      <span className={`text-xs ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}>
                        <i className="fas fa-info-circle mr-1"></i>
                        {ratingRange.min || 0} - {ratingRange.max || "∞"}
                      </span>
                      <button
                        onClick={() => setRatingRange({ min: "", max: "" })}
                        className={`text-xs ${isDarkMode ? "text-blue-300 hover:text-blue-200" : "text-blue-600 hover:text-blue-800"}`}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Filter Card */}
              <div
                className={`rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] ${
                  isDarkMode ? "bg-slate-700/30 border border-slate-600/50" : "bg-slate-50/50 border border-slate-200/50"
                }`}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`p-2 rounded-xl ${isDarkMode ? "bg-green-500/20" : "bg-green-100"}`}>
                    <i className="fas fa-check-circle text-green-500"></i>
                  </div>
                  <h3 className={`font-bold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>Status</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { value: "all", label: "All", icon: "fas fa-list", color: "blue" },
                    { value: "solved", label: "Solved", icon: "fas fa-check", color: "green" },
                    { value: "unsolved", label: "Unsolved", icon: "fas fa-clock", color: "orange" },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center p-3 rounded-xl cursor-pointer transition-all duration-300 hover:scale-[1.02] ${
                        statusFilter === option.value
                          ? isDarkMode
                            ? `bg-${option.color}-500/20 border border-${option.color}-500/50`
                            : `bg-${option.color}-50 border border-${option.color}-200`
                          : isDarkMode
                          ? "bg-slate-800/50 hover:bg-slate-700/50"
                          : "bg-white hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="statusFilter"
                        value={option.value}
                        checked={statusFilter === option.value}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="sr-only"
                      />
                      <div className={`p-2 rounded-lg mr-3 ${isDarkMode ? `bg-${option.color}-500/20` : `bg-${option.color}-100`}`}>
                        <i className={`${option.icon} text-${option.color}-500 text-sm`}></i>
                      </div>
                      <span className={`font-medium ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>{option.label}</span>
                      {statusFilter === option.value && (
                        <div className="ml-auto">
                          <i className={`fas fa-check text-${option.color}-500`}></i>
                        </div>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {/* Sort Options Card */}
              <div
                className={`rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] ${
                  isDarkMode ? "bg-slate-700/30 border border-slate-600/50" : "bg-slate-50/50 border border-slate-200/50"
                }`}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`p-2 rounded-xl ${isDarkMode ? "bg-purple-500/20" : "bg-purple-100"}`}>
                    <i className="fas fa-sort text-purple-500"></i>
                  </div>
                  <h3 className={`font-bold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>Sort By</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 ${
                        isDarkMode ? "bg-slate-800/70 border-slate-600/50 text-slate-100" : "bg-white border-slate-200 text-slate-900"
                      }`}
                    >
                      <option value="rating">Rating</option>
                      <option value="solvedCount">Solved Count</option>
                      <option value="name">Name</option>
                      <option value="contestId">Contest ID</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`text-sm font-medium ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Order:</span>
                    <button
                      onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 hover:scale-105 ${
                        isDarkMode ? "bg-slate-800/70 hover:bg-slate-700/70 text-slate-200" : "bg-white hover:bg-slate-50 text-slate-700 border border-slate-200"
                      }`}
                    >
                      <i className={`fas fa-sort-${sortOrder === "asc" ? "up" : "down"} text-purple-500`}></i>
                      <span className="text-sm font-medium">{sortOrder === "asc" ? "Ascending" : "Descending"}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Tag Mode Card - Full Width Section */}
            <div className="lg:col-span-2 xl:col-span-4 mt-6">
              <div
                className={`rounded-2xl p-6 transition-all duration-300 ${isDarkMode ? "bg-slate-700/30 border border-slate-600/50" : "bg-slate-50/50 border border-slate-200/50"}`}
              >
                <div className="flex items-center space-x-3 mb-6">
                  <div className={`p-2 rounded-xl ${isDarkMode ? "bg-indigo-500/20" : "bg-indigo-100"}`}>
                    <i className="fas fa-tags text-indigo-500"></i>
                  </div>
                  <h3 className={`font-bold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>Tag Filter Mode</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { value: "include", label: "Include All", desc: "Must have all selected tags", icon: "fas fa-check-double", color: "blue" },
                    { value: "includeOnly", label: "Exact Match", desc: "Only the selected tags", icon: "fas fa-equals", color: "cyan" },
                    { value: "doesNotInclude", label: "Exclude", desc: "None of the selected tags", icon: "fas fa-times", color: "red" },
                    { value: "includeSome", label: "Include Any", desc: "At least one selected tag", icon: "fas fa-check", color: "green" },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`relative p-4 rounded-xl cursor-pointer transition-all duration-300 hover:scale-[1.02] border-2 ${
                        tagMode === option.value
                          ? isDarkMode
                            ? option.color === "blue"
                              ? "bg-blue-500/20 border-blue-500/50"
                              : option.color === "cyan"
                              ? "bg-cyan-500/20 border-cyan-500/50"
                              : option.color === "red"
                              ? "bg-red-500/20 border-red-500/50"
                              : "bg-green-500/20 border-green-500/50"
                            : option.color === "blue"
                            ? "bg-blue-50 border-blue-200"
                            : option.color === "cyan"
                            ? "bg-cyan-50 border-cyan-200"
                            : option.color === "red"
                            ? "bg-red-50 border-red-200"
                            : "bg-green-50 border-green-200"
                          : isDarkMode
                          ? "bg-slate-800/50 border-slate-600/50 hover:border-slate-500/50"
                          : "bg-white border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <input type="radio" name="tagMode" value={option.value} checked={tagMode === option.value} onChange={(e) => setTagMode(e.target.value)} className="sr-only" />
                      <div className="text-center">
                        <div
                          className={`p-3 rounded-lg mx-auto w-fit mb-3 ${
                            isDarkMode
                              ? option.color === "blue"
                                ? "bg-blue-500/20"
                                : option.color === "cyan"
                                ? "bg-cyan-500/20"
                                : option.color === "red"
                                ? "bg-red-500/20"
                                : "bg-green-500/20"
                              : option.color === "blue"
                              ? "bg-blue-100"
                              : option.color === "cyan"
                              ? "bg-cyan-100"
                              : option.color === "red"
                              ? "bg-red-100"
                              : "bg-green-100"
                          }`}
                        >
                          <i
                            className={`${option.icon} ${
                              option.color === "blue" ? "text-blue-500" : option.color === "cyan" ? "text-cyan-500" : option.color === "red" ? "text-red-500" : "text-green-500"
                            } text-lg`}
                          ></i>
                        </div>
                        <div className={`font-bold text-sm mb-1 ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>{option.label}</div>
                        <div className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>{option.desc}</div>
                      </div>
                      {tagMode === option.value && (
                        <div className="absolute top-2 right-2">
                          <div
                            className={`w-3 h-3 rounded-full ${
                              option.color === "blue" ? "bg-blue-500" : option.color === "cyan" ? "bg-cyan-500" : option.color === "red" ? "bg-red-500" : "bg-green-500"
                            }`}
                          ></div>
                        </div>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Tags Section */}
          {availableTags.length > 0 && (
            <div className={`mt-6 rounded-2xl p-6 ${isDarkMode ? "bg-slate-700/30 border border-slate-600/50" : "bg-slate-50/50 border border-slate-200/50"}`}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-xl ${isDarkMode ? "bg-emerald-500/20" : "bg-emerald-100"}`}>
                    <i className="fas fa-tags text-emerald-500"></i>
                  </div>
                  <div>
                    <h3 className={`font-bold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>Available Tags</h3>
                    <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                      {availableTags.length} total • {selectedTags.length} selected
                    </p>
                  </div>
                </div>
                {selectedTags.length > 0 && (
                  <button
                    onClick={() => setSelectedTags([])}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:scale-105 ${
                      isDarkMode ? "bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30" : "bg-red-100 text-red-700 border border-red-200 hover:bg-red-200"
                    }`}
                  >
                    <i className="fas fa-times mr-2"></i>
                    Clear Tags
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto pr-2">
                {availableTags.map((tag) => (
                  <button
                    key={tag}
                    className={`px-4 py-2 text-sm rounded-xl transition-all duration-300 cursor-pointer hover:scale-105 whitespace-nowrap font-medium border-2 ${
                      selectedTags.includes(tag)
                        ? isDarkMode
                          ? "bg-blue-500/20 text-blue-300 border-blue-500/50 shadow-lg"
                          : "bg-blue-100 text-blue-800 border-blue-300 shadow-lg"
                        : isDarkMode
                        ? "bg-slate-800/50 text-slate-300 border-slate-600/50 hover:bg-slate-700/50 hover:border-blue-400/50"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:border-blue-200"
                    }`}
                    onClick={() => handleTagClick(tag)}
                  >
                    {selectedTags.includes(tag) && <i className="fas fa-check mr-2 text-xs"></i>}
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Results Info */}
        <div className={`text-center mb-8 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
          <p className="text-lg">
            Showing <span className="font-semibold">{filteredProblems.length.toLocaleString()}</span> problems
          </p>
        </div>

        {/* Problems Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
          {currentProblems.map((problem) => {
            return (
              <div
                key={`${problem.contestId}-${problem.index}`}
                className={`rounded-3xl backdrop-blur-xl p-8 transition-all duration-500 hover:scale-105 cursor-pointer ${
                  isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl hover:shadow-3xl" : "bg-white/80 border border-slate-200/50 shadow-xl hover:shadow-2xl"
                }`}
                onClick={() => openProblemDetails(problem)}
              >
                {/* Problem Header */}
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center space-x-2">
                    <span className={`text-lg font-bold ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}>
                      {problem.contestId}
                      {problem.index}
                    </span>
                    {problem.rating && <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRatingColor(problem.rating)}`}>{problem.rating}</span>}
                  </div>
                  <div className="flex items-center space-x-2">
                    {problem.isSolved && (
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isDarkMode ? "bg-green-500/20 text-green-400 border border-green-500/30" : "bg-green-100 text-green-700 border border-green-200"
                        }`}
                      >
                        <i className="fas fa-check mr-1"></i>
                        Solved
                      </span>
                    )}
                  </div>
                </div>

                {/* Problem Name */}
                <h3 className={`text-xl font-semibold mb-6 line-clamp-2 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{problem.name}</h3>

                {/* Difficulty and Solved Count */}
                <div className="flex items-center justify-between mb-6">
                  {problem.rating && (
                    <div className={`px-3 py-2 rounded-xl ${isDarkMode ? "bg-slate-700/50" : "bg-slate-100"}`}>
                      <span className={`text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                        <i className="fas fa-user mr-2"></i>
                        {getDifficultyText(problem.rating)}
                      </span>
                    </div>
                  )}
                  <div className={`px-3 py-2 rounded-xl ${isDarkMode ? "bg-slate-700/50" : "bg-slate-100"}`}>
                    <span className={`text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                      <i className="fas fa-users mr-2"></i>
                      {problem.solvedCount.toLocaleString()} solved
                    </span>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-6 max-h-24 overflow-hidden">
                  {problem.tags.slice(0, 3).map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className={`px-3 py-1 text-xs rounded-full transition-all duration-300 cursor-pointer hover:scale-105 whitespace-nowrap font-medium ${
                        isDarkMode ? "bg-slate-700 text-slate-300 hover:bg-slate-600" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTagClick(tag);
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                  {problem.tags.length > 3 && (
                    <span className={`px-3 py-1 text-xs rounded-full font-medium ${isDarkMode ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"}`}>
                      +{problem.tags.length - 3}
                    </span>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openProblemDetails(problem);
                    }}
                    className={`flex-1 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:scale-105 ${
                      isDarkMode
                        ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                        : "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                    }`}
                  >
                    <i className="fas fa-info-circle mr-2"></i>
                    Details
                  </button>
                  <a
                    href={`https://codeforces.com/problemset/problem/${problem.contestId}/${problem.index}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className={`flex-1 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:scale-105 ${
                      isDarkMode
                        ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                        : "bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white"
                    }`}
                  >
                    <i className="fas fa-external-link-alt mr-2"></i>
                    Solve
                  </a>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTagClick(problem.tags[0]);
                    }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:scale-105 ${
                      isDarkMode ? "bg-slate-700 hover:bg-slate-600 text-slate-300" : "bg-slate-100 hover:bg-slate-200 text-slate-600"
                    }`}
                    title="Filter by first tag"
                  >
                    <i className="fas fa-filter"></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-3 mb-8">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-6 py-3 border-2 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 ${
                isDarkMode ? "bg-slate-800/50 border-slate-600 hover:bg-slate-700/50 text-slate-300" : "bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
              }`}
            >
              <i className="fas fa-chevron-left mr-2"></i>
              Previous
            </button>

            {/* Page Numbers */}
            <div className="flex space-x-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`px-4 py-3 rounded-xl transition-all duration-300 hover:scale-105 ${
                      currentPage === pageNumber
                        ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                        : isDarkMode
                        ? "bg-slate-800/50 border border-slate-600 hover:bg-slate-700/50 text-slate-300"
                        : "bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-6 py-3 border-2 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 ${
                isDarkMode ? "bg-slate-800/50 border-slate-600 hover:bg-slate-700/50 text-slate-300" : "bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
              }`}
            >
              Next
              <i className="fas fa-chevron-right ml-2"></i>
            </button>
          </div>
        )}

        {/* Page Info */}
        {totalPages > 1 && (
          <div className={`text-center text-sm mb-8 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
            Page {currentPage} of {totalPages} • Showing {indexOfFirstProblem + 1}-{Math.min(indexOfLastProblem, filteredProblems.length)} of{" "}
            {filteredProblems.length.toLocaleString()} problems
          </div>
        )}

        {/* No Results */}
        {filteredProblems.length === 0 && (
          <div className="text-center py-16">
            <div className={`p-6 rounded-full mb-6 inline-block ${isDarkMode ? "bg-slate-800/50" : "bg-slate-100"}`}>
              <i className={`text-4xl ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                <i className="fas fa-search"></i>
              </i>
            </div>
            <p className={`text-xl font-semibold mb-2 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>No problems found matching your criteria</p>
            <p className={`${isDarkMode ? "text-slate-500" : "text-slate-500"}`}>Try adjusting your filters</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProblemsList;
